/**
 * LoanCarHistoryModal.jsx
 *
 * Full incident history for a single loan car rego plate.
 * - Newest-first, 20 incidents per page (server-paginated)
 * - Each incident shows: direction badge, date/time, booking ref,
 *   odometer, insurance flag, notes, and expandable image gallery
 * - OUT incidents are cyan, IN incidents are emerald — visually pairing
 *   each round-trip at a glance
 *
 * Usage (in ResourcesPage):
 *   import LoanCarHistoryModal from './LoanCarHistoryModal';
 *
 *   {showHistoryModal && (
 *     <LoanCarHistoryModal
 *       car={historyCarDetails}
 *       incidents={historyIncidents}
 *       page={historyPage}
 *       totalPages={historyTotalPages}
 *       totalElements={historyTotalElements}
 *       loading={historyLoading}
 *       error={historyError}
 *       onPageChange={(p) => dispatch(fetchLoanCarIncidentHistory({ rego: historyCarDetails.rego, page: p }))}
 *       onClose={() => dispatch(closeHistoryModal())}
 *     />
 *   )}
 */

import React, { useState } from 'react';
import {
    X, Loader2, AlertCircle, ArrowUpRight, ArrowDownLeft,
    Gauge, Shield, FileText, ChevronLeft, ChevronRight,
    Image as ImageIcon, ChevronDown, ChevronUp, Car,
    Clock, Hash,
} from 'lucide-react';
import { safeNoteHtml } from '../utils/safeHtml';

// ─── Helpers ────────────────────────────────────────────────────────────────

const CATEGORY_LABELS = {
    DRIVING_LICENCE: 'Driving Licence',
    INSURANCE_CERTIFICATE: 'Insurance Certificate',
    CAR_DASHBOARD_ON_VEHICLE_START: 'Dashboard on Start',
    OTHER: 'Other Photos',
};

function formatDateTime(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-AU', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    });
}

function formatDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-AU', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
}

function totalImageCount(imagesByCategory) {
    if (!imagesByCategory) return 0;
    return imagesByCategory.reduce(
        (sum, cat) => sum + (cat.images?.length ?? 0), 0
    );
}

// ─── Image Gallery ────────────────────────────────────────────────────────────

const ImageGallery = ({ imagesByCategory }) => {
    const [lightbox, setLightbox] = useState(null); // presignedUrl

    if (!imagesByCategory || imagesByCategory.every(c => !c.images?.length)) {
        return (
            <p className="text-xs text-gray-400 italic mt-2">No images recorded.</p>
        );
    }

    return (
        <div className="mt-3 space-y-3">
            {imagesByCategory.map((cat) => {
                if (!cat.images?.length) return null;
                return (
                    <div key={cat.category}>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                            {CATEGORY_LABELS[cat.category] ?? cat.category}
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {cat.images.map((img, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setLightbox(img.presignedUrl)}
                                    className="w-20 h-20 rounded-lg overflow-hidden border border-gray-200 hover:border-cyan-400 hover:shadow-md transition-all flex-shrink-0 bg-gray-50"
                                    title="Click to enlarge"
                                >
                                    <img
                                        src={img.presignedUrl}
                                        alt={`${cat.category} ${idx + 1}`}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentNode.innerHTML =
                                                '<div class="w-full h-full flex items-center justify-center"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></div>';
                                        }}
                                    />
                                </button>
                            ))}
                        </div>
                    </div>
                );
            })}

            {/* Lightbox */}
            {lightbox && (
                <div
                    className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4"
                    onClick={() => setLightbox(null)}
                >
                    <div className="relative max-w-4xl max-h-[90vh]" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setLightbox(null)}
                            className="absolute -top-10 right-0 text-white hover:text-gray-300"
                        >
                            <X size={24} />
                        </button>
                        <img
                            src={lightbox}
                            alt="Enlarged incident photo"
                            className="max-w-full max-h-[85vh] object-contain rounded-xl shadow-2xl"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Single Incident Card ────────────────────────────────────────────────────

const IncidentCard = ({ incident, index }) => {
    const [expanded, setExpanded] = useState(false);
    const isOut = incident.type === 'OUT';
    const imgCount = totalImageCount(incident.imagesByCategory);

    const directionConfig = isOut ? {
        label: 'OUT — Vehicle Handed Out',
        icon: ArrowUpRight,
        badgeBg: 'bg-cyan-100',
        badgeText: 'text-cyan-700',
        borderAccent: 'border-l-cyan-400',
        iconBg: 'bg-cyan-50',
        iconColor: 'text-cyan-600',
    } : {
        label: 'IN — Vehicle Returned',
        icon: ArrowDownLeft,
        badgeBg: 'bg-emerald-100',
        badgeText: 'text-emerald-700',
        borderAccent: 'border-l-emerald-400',
        iconBg: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
    };

    const DirIcon = directionConfig.icon;

    return (
        <div className={`bg-white rounded-xl border border-gray-200 border-l-4 ${directionConfig.borderAccent} shadow-sm hover:shadow-md transition-shadow`}>
            {/* Card Header — always visible */}
            <div className="p-4">
                <div className="flex items-start gap-3">
                    {/* Direction icon */}
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${directionConfig.iconBg}`}>
                        <DirIcon size={18} className={directionConfig.iconColor} />
                    </div>

                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${directionConfig.badgeBg} ${directionConfig.badgeText}`}>
                                <DirIcon size={11} />
                                {incident.type}
                            </span>
                            {incident.insuranceTaken && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                    <Shield size={11} /> Insurance
                                </span>
                            )}
                        </div>

                        {/* Date + booking ref row */}
                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm">
                            <span className="flex items-center gap-1 text-gray-700 font-medium">
                                <Clock size={13} className="text-gray-400" />
                                {formatDateTime(incident.recordedAt)}
                            </span>
                            {incident.bookingRef && (
                                <span className="flex items-center gap-1 text-gray-500">
                                    <Hash size={13} className="text-gray-400" />
                                    {incident.bookingRef}
                                </span>
                            )}
                        </div>

                        {/* Odometer */}
                        {incident.odometerReading != null && (
                            <div className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
                                <Gauge size={13} className="text-gray-400" />
                                <span>{incident.odometerReading.toLocaleString()} km</span>
                            </div>
                        )}
                    </div>

                    {/* Expand toggle */}
                    <button
                        onClick={() => setExpanded(v => !v)}
                        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 flex-shrink-0 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        {imgCount > 0 && (
                            <span className="flex items-center gap-0.5 text-gray-400">
                                <ImageIcon size={12} />
                                {imgCount}
                            </span>
                        )}
                        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                </div>

                {/* Notes preview (single line, always shown if present) */}
                {incident.notes && !expanded && (
                    <p className="mt-2 text-sm text-gray-500 line-clamp-1 pl-12">
                        <FileText size={12} className="inline mr-1 text-gray-400" />
                        {incident.notes.replace(/<[^>]+>/g, '')}
                    </p>
                )}
            </div>

            {/* Expanded section */}
            {expanded && (
                <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                    {/* Full notes */}
                    {incident.notes && (
                        <div className="mb-3">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Notes</p>
                            <div
                                className="text-sm text-gray-700 leading-relaxed"
                                dangerouslySetInnerHTML={safeNoteHtml(incident.notes)}
                            />
                        </div>
                    )}

                    {/* Images */}
                    <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1">
                            <ImageIcon size={12} /> Photos
                        </p>
                        <ImageGallery imagesByCategory={incident.imagesByCategory} />
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Pagination Bar ──────────────────────────────────────────────────────────

const PaginationBar = ({ page, totalPages, totalElements, onPageChange, loading }) => {
    if (totalPages <= 1) return null;

    const PAGE_SIZE = 20;
    const from = page * PAGE_SIZE + 1;
    const to = Math.min((page + 1) * PAGE_SIZE, totalElements);

    return (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
            <p className="text-sm text-gray-500">
                Showing <span className="font-medium text-gray-700">{from}–{to}</span> of{' '}
                <span className="font-medium text-gray-700">{totalElements}</span> incidents
            </p>
            <div className="flex items-center gap-2">
                <button
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 0 || loading}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-white border border-gray-200 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <ChevronLeft size={15} /> Prev
                </button>
                <span className="text-sm text-gray-500 px-1">
                    {page + 1} / {totalPages}
                </span>
                <button
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages - 1 || loading}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-white border border-gray-200 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    Next <ChevronRight size={15} />
                </button>
            </div>
        </div>
    );
};

// ─── Main Modal ──────────────────────────────────────────────────────────────

const LoanCarHistoryModal = ({
                                 car,
                                 incidents = [],
                                 page = 0,
                                 totalPages = 0,
                                 totalElements = 0,
                                 loading,
                                 error,
                                 onPageChange,
                                 onClose,
                             }) => {
    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-3">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col shadow-2xl">

                {/* ── Modal Header ── */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-cyan-50 rounded-xl flex items-center justify-center">
                            <Car size={20} className="text-cyan-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">
                                Loan History — <span className="text-cyan-700">{car?.rego}</span>
                            </h2>
                            <p className="text-sm text-gray-500">
                                {[car?.make, car?.model, car?.manufactureYear].filter(Boolean).join(' · ')}
                                {totalElements > 0 && (
                                    <span className="ml-2 text-gray-400">· {totalElements} incident{totalElements !== 1 ? 's' : ''}</span>
                                )}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {/* ── Legend ── */}
                <div className="flex items-center gap-4 px-6 py-2.5 bg-gray-50 border-b border-gray-100 flex-shrink-0">
                    <span className="flex items-center gap-1.5 text-xs text-gray-600">
                        <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 inline-block" />
                        OUT — handed to customer
                    </span>
                    <span className="flex items-center gap-1.5 text-xs text-gray-600">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block" />
                        IN — returned to workshop
                    </span>
                    <span className="text-xs text-gray-400 ml-auto">
                        Click any card to expand photos &amp; notes
                    </span>
                </div>

                {/* ── Body ── */}
                <div className="flex-1 overflow-y-auto px-6 py-4">

                    {/* Loading */}
                    {loading && (
                        <div className="flex flex-col items-center justify-center py-16 gap-3">
                            <Loader2 className="animate-spin text-cyan-500" size={32} />
                            <span className="text-sm text-gray-500">Loading history…</span>
                        </div>
                    )}

                    {/* Error */}
                    {error && !loading && (
                        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                            <AlertCircle size={18} />
                            {error}
                        </div>
                    )}

                    {/* Empty */}
                    {!loading && !error && incidents.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-400 gap-3">
                            <Car size={40} className="text-gray-300" />
                            <p className="text-sm font-medium">No incident records for {car?.rego}</p>
                            <p className="text-xs text-gray-400">Records are created when a loan car is handed out or returned.</p>
                        </div>
                    )}

                    {/* Incident list */}
                    {!loading && !error && incidents.length > 0 && (
                        <div className="space-y-3">
                            {incidents.map((incident, idx) => (
                                <IncidentCard
                                    key={incident.incidentId ?? idx}
                                    incident={incident}
                                    index={idx}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Pagination ── */}
                <PaginationBar
                    page={page}
                    totalPages={totalPages}
                    totalElements={totalElements}
                    onPageChange={onPageChange}
                    loading={loading}
                />
            </div>
        </div>
    );
};

export default LoanCarHistoryModal;